// Display profiles for users loaded on demand by uid, plus the viewer's
// recently viewed users. Recents live in this browser's localStorage only:
// they are a convenience, not a shared or durable list.

const RECENT_USERS_KEY = 'offboard.recentUsers';
const MAX_RECENT_USERS = 20;

/**
 * Initials from a name: first letter of the first and last words.
 * @param {string|null} name
 * @returns {string} Initials, or '?' when there is no name
 */
export const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '?';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

/**
 * Convert a `searchFirebaseUsers` result into the profile shape used everywhere
 * else ({ uid, name, email, profilePicturePath, initials }).
 */
export const profileFromSearchResult = (result) => {
  const name = result.firestoreName || result.displayName || null;
  return {
    uid: result.uid,
    name,
    email: result.email || null,
    profilePicturePath: result.profilePicturePath || null,
    initials: name ? getInitials(name) : null
  };
};

const readRecents = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(RECENT_USERS_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter(u => u && typeof u.uid === 'string') : [];
  } catch {
    return [];
  }
};

const writeRecents = (users) => {
  try {
    window.localStorage.setItem(RECENT_USERS_KEY, JSON.stringify(users.slice(0, MAX_RECENT_USERS)));
  } catch {
    // Storage unavailable (private window, blocked site data): recents just don't persist
  }
};

/** Recently viewed users, most recent first. */
export const getRecentUsers = () => readRecents();

/** Move a user to the front of the recents, storing their latest profile. */
export const addRecentUser = (profile) => {
  if (!profile?.uid) return;
  const entry = {
    uid: profile.uid,
    name: profile.name || null,
    email: profile.email || null,
    profilePicturePath: profile.profilePicturePath || null,
    initials: profile.initials || null
  };
  writeRecents([entry, ...readRecents().filter(u => u.uid !== profile.uid)]);
};

/** Drop a user from the recents. Returns the remaining list. */
export const removeRecentUser = (uid) => {
  const remaining = readRecents().filter(u => u.uid !== uid);
  writeRecents(remaining);
  return remaining;
};

/**
 * Replace stored recents with freshly loaded profiles, keeping order. Entries
 * with no fresh profile are kept as stored. Returns the updated list.
 */
export const refreshRecentUsers = (profiles) => {
  const byUid = new Map(profiles.map(p => [p.uid, p]));
  const updated = readRecents().map(u => {
    const fresh = byUid.get(u.uid);
    return fresh ? { ...u, ...fresh } : u;
  });
  writeRecents(updated);
  return updated;
};
